// Every time on the site is shown in the user's zone from the account
// settings. `useTime()` binds the helpers in lib/time to that zone.
import { useMemo } from 'react';
import { useT } from '@/i18n';
import * as T from '@/lib/time';
import { useAccount } from './queries';

export function useTime() {
  // the web display zone (web-only: it never changes the Telegram schedule)
  const tz = useAccount().web.displayTimezone;
  // the language i18n actually speaks (it switches right after the account's)
  const language = useT().i18n.language;
  return useMemo(() => ({
    tz,
    /** МСК / UTC+2 … */
    zone: T.tzShort(tz),
    time: (v: string | number) => T.time(v, tz),
    dayLabel: (v: string | number) => T.dayLabel(v, tz),
    dayShort: (v: string | number) => T.dayShort(v, tz),
    dayDiff: (v: string | number) => T.dayDiff(v, tz),
    isToday: (v: string | number) => T.isToday(v, tz),
    dateKey: (v: string | number) => T.dateKey(v, tz),
    dateLong: (v: string | number) => T.dateLong(v, tz),
    dateFull: (v: string | number) => T.dateFull(v, tz),
    dateShort: (v: string | number) => T.dateShort(v, tz),
    monthName: (v: string | number) => T.monthName(v, tz),
    dateTime: (v: string | number) => T.dateTime(v, tz),
    when: (v: string | number) => T.when(v, tz),
    /** today: 'HH:MM', otherwise 'вчера 18:00' / 'пн 18:00' / '4 окт. 18:00' */
    stamp: (v: string | number) => (T.isToday(v, tz) ? T.time(v, tz) : T.when(v, tz)),
    ago: T.ago,
    until: T.until,
    todayKey: () => T.todayKey(tz),
    /** «Сегодня» / «Вчера» / «Пятница, 2 октября» — a day heading in the user's zone */
    groupLabel: (v: string | number) => (T.dayDiff(v, tz) >= -1 && T.dayDiff(v, tz) <= 1 ? T.dayLabel(v, tz) : `${T.dayLabel(v, tz)}, ${T.dateLong(v, tz)}`),
    /** items grouped by their local day, newest day first (input order kept inside a day) */
    byDay: <X extends { at: string }>(xs: X[]) => {
      const m = new Map<string, X[]>();
      xs.forEach((x) => { const k = T.dateKey(x.at, tz); m.set(k, [...(m.get(k) ?? []), x]); });
      return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0])).map(([key, items]) => ({ key, items, label: T.dayDiff(items[0].at, tz) >= -1 ? T.dayLabel(items[0].at, tz) : `${T.dayLabel(items[0].at, tz)}, ${T.dateLong(items[0].at, tz)}` }));
    },
    /** local days around today: offsets 0, -1, … (or +1 …) with labels, built in the user's zone */
    days: (from: number, count: number) => Array.from({ length: count }, (_, i) => {
      const off = from - i;
      const key = T.addDays(T.todayKey(tz), off);
      const at = T.zonedToIso(key, '12:00', tz);
      return { off, key, at, label: T.dayLabel(at, tz), short: T.dayShort(at, tz), full: T.dateLong(at, tz) };
    }),
    // labels (weekdays, «today», МСК / MSK) follow the account language
  }), [tz, language]); // eslint-disable-line react-hooks/exhaustive-deps
}
export type TimeFmt = ReturnType<typeof useTime>;
